import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useRouteError } from 'react-router-dom'
import { AppSuspenseFallback } from '@/components/AppSuspenseFallback'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/use-translation'
import { analytics } from '@/lib/analytics/analytics.service'
import { isChunkNotFoundError } from '@/lib/utils/lazy-with-retry'

/** Loop guard: a chunk reload that happened within this window is treated as "already tried". */
const CHUNK_RELOAD_KEY = 'chunk-load-retry'
const CHUNK_RELOAD_WINDOW_MS = 30_000

function toError(error: unknown): Error {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  if (error && typeof error === 'object') {
    const maybeMessage = (error as { message?: unknown }).message
    if (typeof maybeMessage === 'string') return new Error(maybeMessage)
  }
  return new Error('Unknown route error')
}

/**
 * Route-level error boundary for use as `errorElement` on the router tree.
 *
 * Differs from `<ErrorBoundary>`: that one is a class component that wraps
 * children (catches render errors in the tree); this one reads the error from
 * React Router via `useRouteError`. Wired per-route it renders inside the app
 * shell so the sidebar and navigation remain usable.
 *
 * Handles two cases:
 * 1. Chunk-load failures (stale assets after a new deploy) → reload once to
 *    fetch fresh chunks, guarded against infinite loops. A class boundary
 *    placed outside `<RouterProvider>` never receives these, so the recovery
 *    logic lives here where React Router actually routes the error.
 * 2. Any other route error → branded error card with go-back / reload actions.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()
  const t = useTranslation()

  const normalized = toError(error)
  const isChunkError = isChunkNotFoundError(normalized)

  // A new deploy ships fresh chunks, so a single reload usually fixes a stale
  // import. We only fall back to a manual prompt if a recent reload already
  // failed — meaning reloading again won't help.
  const [chunkReloadFailed, setChunkReloadFailed] = useState(false)

  useEffect(() => {
    if (isChunkError) return
    console.error('[RouteErrorBoundary] Route error:', {
      message: normalized.message,
      stack: normalized.stack,
    })
    analytics.captureException(normalized, { source: 'RouteErrorBoundary' })
  }, [isChunkError, normalized])

  useEffect(() => {
    if (!isChunkError) return

    const lastReload = Number.parseInt(localStorage.getItem(CHUNK_RELOAD_KEY) ?? '0', 10)
    const reloadedRecently = Date.now() - lastReload < CHUNK_RELOAD_WINDOW_MS

    if (reloadedRecently) {
      // A reload already happened and the chunk is still missing — reloading
      // again won't help. Keep the timestamp (so re-runs stay on this branch
      // and don't loop); it's cleared only when the user retries explicitly.
      console.error('[RouteErrorBoundary] Reload did not fix the chunk error, prompting user')
      analytics.captureException(normalized, {
        source: 'RouteErrorBoundary',
        chunkReloadFailed: true,
      })
      setChunkReloadFailed(true)
      return
    }

    console.warn('[RouteErrorBoundary] Chunk load failed (likely new deploy), reloading for fresh assets')
    localStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
  }, [isChunkError, normalized])

  if (isChunkError && !chunkReloadFailed) {
    return <AppSuspenseFallback />
  }

  if (isChunkError && chunkReloadFailed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className="max-w-md w-full bg-white border border-neutral-200 rounded-xl p-6 sm:p-8 text-center flex flex-col gap-4">
          <div className="flex justify-center">
            <div className="flex items-center justify-center size-12 rounded-full bg-primary/10">
              <RefreshCw className="size-6 text-primary" />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('errorBoundary.chunk.title')}
            </h2>
            <p className="text-sm text-neutral-500">{t('errorBoundary.chunk.subtitle')}</p>
          </div>

          <Button
            onClick={() => {
              localStorage.removeItem(CHUNK_RELOAD_KEY)
              window.location.reload()
            }}
            className="justify-center"
          >
            <RefreshCw className="size-4" />
            {t('errorBoundary.chunk.reload')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      <div className="max-w-md w-full bg-white border border-neutral-200 rounded-xl p-6 sm:p-8 text-center flex flex-col gap-4">
        <div className="flex justify-center">
          <div className="flex items-center justify-center size-12 rounded-full bg-destructive/10">
            <AlertTriangle className="size-6 text-destructive" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <h2 className="text-lg font-semibold text-neutral-900">
            {t('errorBoundary.title')}
          </h2>
          <p className="text-sm text-neutral-500">{t('errorBoundary.subtitle')}</p>
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-left">
          <p className="text-[11px] uppercase tracking-wide font-medium text-neutral-400 mb-1">
            {t('errorBoundary.technicalErrorLabel')}
          </p>
          <code className="text-xs text-destructive font-mono break-words block">
            {normalized.message || t('errorBoundary.unknownError')}
          </code>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1 justify-center"
          >
            {t('errorBoundary.buttons.goBack')}
          </Button>
          <Button
            onClick={() => window.location.reload()}
            className="flex-1 justify-center"
          >
            <RefreshCw className="size-4" />
            {t('errorBoundary.buttons.reload')}
          </Button>
        </div>
      </div>
    </div>
  )
}
